import { useEffect } from 'react';
import {
  eventDigit,
  eventLetter,
  isAppModalOpen,
  isModKey,
  isTypingTarget,
} from '../utils/keyboard.js';

const settingsCards = () => Array.from(document.querySelectorAll('[data-settings-card]'));

const focusedCard = () => {
  const active = document.activeElement;
  if (!active) return null;
  return active.closest?.('[data-settings-card]') || null;
};

export default function useCompanySettingsHotkeys({
  enabled,
  visibleCategories,
  currentCategory,
  onCategoryChange,
  onSave,
  saving,
}) {
  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      const typing = isTypingTarget(event.target);
      const helpOpen = Boolean(document.querySelector('[data-shortcuts-help="true"]'));
      if (helpOpen) return;

      if (isModKey(event) && !event.altKey && !event.shiftKey && eventLetter(event) === 's') {
        event.preventDefault();
        if (!saving) onSave?.();
        return;
      }

      if (typing || isAppModalOpen()) return;

      const cats = visibleCategories || [];
      const digit = eventDigit(event);
      if (digit && !isModKey(event) && !event.altKey && !event.shiftKey) {
        const idx = Number(digit) - 1;
        if (idx >= 0 && idx < cats.length) {
          event.preventDefault();
          onCategoryChange?.(cats[idx].id);
        }
        return;
      }

      if (event.key === '[' || event.key === ']') {
        if (!cats.length) return;
        event.preventDefault();
        const currentIdx = Math.max(0, cats.findIndex((c) => c.id === currentCategory));
        const nextIdx =
          event.key === ']'
            ? (currentIdx + 1) % cats.length
            : (currentIdx - 1 + cats.length) % cats.length;
        onCategoryChange?.(cats[nextIdx].id);
        return;
      }

      const letter = eventLetter(event);
      if ((letter === 'j' || letter === 'k') && !isModKey(event) && !event.altKey) {
        const list = settingsCards();
        if (!list.length) return;
        event.preventDefault();
        const current = focusedCard();
        const idx = current ? list.indexOf(current) : -1;
        const delta = letter === 'j' ? 1 : -1;
        const next = idx < 0 ? 0 : (idx + delta + list.length) % list.length;
        list[next].focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, visibleCategories, currentCategory, onCategoryChange, onSave, saving]);
}
